import { useQueryClient } from '@tanstack/react-query'
import { useQueryManagerReviewRules } from 'src/feature/ManagerReview/managerReviewRules.queries'
import { ExpensePolicyResponse } from 'src/models/organizations.models'
import { useHasSaasFeatures } from 'src/queries/organizations/saas.queries'
import { orgQueryKeys } from 'src/queries/queries.constants'
import {
  ExtendBaseQueryOptions,
  MutationMethod,
  RequestConfig,
  useExtendMutation,
  useExtendQuery,
} from 'src/queries/queries.utils'
import { useQueryOwnUser } from 'src/queries/users.queries'

interface QueryOrgExpensePolicyOptions
  extends ExtendBaseQueryOptions<ExpensePolicyResponse> {
  orgId: string | undefined
}

export const useQueryExpensePolicy = ({
  orgId,
  enabled = true,
  ...rest
}: QueryOrgExpensePolicyOptions) => {
  return useExtendQuery({
    queryKey: orgQueryKeys.expensePolicy(orgId),
    queryUrl: () => `/organizations/${orgId}/expensepolicy`,
    enabled: enabled && orgId !== undefined,
    ...rest,
  })
}

export const useAIPolicyAssistantEnabled = (): boolean => {
  const { data: user } = useQueryOwnUser({})

  const hasSaasFeatures = useHasSaasFeatures()

  const { data: policyAssistantRules } = useQueryManagerReviewRules({
    orgId: user?.organizationId,
    // Conditionally enable this query for only Pro/Enterprise tiers or Saas-excluded orgs,
    // otherwise this will show the SaaS upgrade modal on every render
    enabled: hasSaasFeatures,
  })

  return !!policyAssistantRules?.policyAssistantEnabled
}

export const usePostExpensePolicy = ({
  orgId,
}: QueryOrgExpensePolicyOptions) => {
  const queryClient = useQueryClient()
  return useExtendMutation<ExpensePolicyResponse, { body: FormData }>(
    MutationMethod.POST,
    {
      mutationUrl: () => `/organizations/${orgId}/expensepolicy`,
      requestConfig: RequestConfig.Form,
      onSuccess: (data) => {
        queryClient.setQueryData<ExpensePolicyResponse>(
          orgQueryKeys.expensePolicy(data.organizationId),
          data,
        )
      },
    },
  )
}
